/**
 * Shared prose blocks reused across blueprint-related AI prompts.
 *
 * Both the joined blueprint extraction
 * ({@link project-file-blueprint-joined-extraction-prompt.helpers.ts}) and the
 * batch consolidation merger
 * ({@link project-file-batch-merge-prompt.helpers.ts}) need the same
 * Swedish-drawing vocabulary and the same per-storey enumeration rules so
 * that their interpretations stay aligned. Defining them here keeps the two
 * prompts consistent and avoids drift when we tweak the wording.
 */

/**
 * Definitions for the most common Swedish blueprint sheet labels. Generic
 * across projects — do not name specific sites here.
 */
export const SWEDISH_BLUEPRINT_SHEET_VOCABULARY_LINES: readonly string[] = [
  'Swedish drawing vocabulary:',
  '- "Entréplan" / "Bottenplan" / "Bottenvåning" = the building\'s GROUND floor (the entrance level). Always part of the building volume. It often holds parking garage, entry hall, teknik, förråd, cykelrum, etc. Never treat it as below-grade or as a separate detached structure, even on sloped sites where it can be partly tucked into the slope.',
  '- "Plan N" is that storey\'s own sheet. It may be the project\'s floor numbering. Read the entrance from the documents. Do not infer it as one below the next numbered plan. Plans below the entrance stay below it. Keep every plan the upload shows.',
  '- "(+7)" / "(+20)" after a plan name is an elevation stamp in metres, not a storey index and not a second building.',
  '- "Gårdsplan" = plan view of the outdoor courtyard. Open to the air, NOT a building storey, does not by itself imply a slab/garage below.',
  '- "Normalplan" / "Typplan" fills storeys that have no sheet of their own. It is not a numbered plan and not an apartment type.',
  '- "Garageplan" / "Källarplan" sits at the bottom, below ground when it is a basement or garage level.',
  '- "Översta plan" = the top storey, sometimes set back from the storeys below.',
  '- "Sektion" = vertical cut; reliable for storey count and total height. On sloped sites the entréplan often sits half-sunk into the slope (one façade meets street/grade, the opposite façade meets a raised yard above the entréplan slab) — this is ONE building with the entréplan as its ground floor, never two separate volumes.',
];

/**
 * Storey area, read from the documents. A set storey index stays.
 */
export const STOREY_AREA_RULE = `A floor is a storey when the documents give that floor a storey area. Storey area is the area of the floor itself. The building's total area is not a storey area. An area on the roof, including a roof terrace, is not a storey area.

Do not drop a storey index that was set. A named roof level can still be only a roof: if it has no storey area, it is a top view and not an extra storey. A floor that has a storey area stays a storey, even when the area is small.`;

/**
 * Generic rules requiring per-storey enumeration in `accurate_description` /
 * `description_for_design_ai`. Both joined extraction and merger output must
 * preserve every distinct storey footprint, not collapse to a single outline.
 */
export const STOREY_ENUMERATION_RULES_LINES: readonly string[] = [
  'Describe EVERY distinct storey shape, not just the typical one:',
  `- ${STOREY_AREA_RULE}`,
  '- Walk the plans the batch contains, from below the entrance through the entrance to the floors above. A missing entrance drawing does not drop the other plans.',
  '- For each storey whose footprint differs, say which storey and how the outline differs (set-back, overhang, or a second mass). Multi-mass storeys list each mass. Do not state a length or a depth.',
  '- When several storeys share the same footprint, describe them once and say which storeys they cover.',
  '- Do not collapse the building into a single outline by picking the most typical storey — every distinct mass matters for the downstream design AI.',
];
